import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChevronDown,
  faPlus,
  faTimes,
  faLayerGroup,
  faChevronLeft,
} from '@fortawesome/free-solid-svg-icons';
import FormInput from '@frontend/Components/Forms/FormInput/FormInput';
import FormSelect from '@frontend/Components/Forms/FormSelect/FormSelect';
import AutoGenerateButton from '@frontend/Components/AutoGenerateButton';
import SectionCard from '@frontend/Components/SectionCard';
import TeamMembersPicker from '@frontend/Components/TeamMembersPicker';
import FileDropzone from '@frontend/Components/FileDropzone';
import CustomFieldsTablesPanel from '@frontend/Components/CustomFieldsTablesPanel';
import ClientService from '@frontend/AppSettings/services/ClientService';
import EmployeeService from '@frontend/AppSettings/services/EmployeeService';
import WorkflowService from '@frontend/AppSettings/services/WorkflowService';
import CustomFieldsService from '@frontend/AppSettings/services/CustomfieldsService';
import TablesService from '@frontend/AppSettings/services/TablesService';
import MatterService from '@frontend/AppSettings/services/MatterService';
import MatterCostCodeBudgetService from '@frontend/AppSettings/services/MatterCostCodeBudgetService';
import CostCodeBudgetInputs from '@frontend/Financial/CostCodeBudgets/CostCodeBudgetInputs';
import toastService from '@frontend/AppSettings/services/ToastService';
import './CreateMattersPage.css';
import { Client } from '@frontend/Models/Client/ClientModels';
import { Employees } from '@frontend/Models/Employee/EmployeeModels';
import { Workflow } from '@frontend/Models/Workflow/WorkflowModels';
import { CustomField } from '@frontend/Models/CustomField/CustomFieldModels';
import { GroupRule } from '@frontend/Models/GroupRule/GroupRuleModels';
import { CustomTable } from '@frontend/Models/Table/CustomTableModels';
import { MatterFormData } from '@frontend/Models/Matter/MatterModels';
import { SelectOption } from '@frontend/Models/Form/FormModels';
import FormEntryMultilineTextbox from '@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox';
import WorkflowManagerModal from './Components/WorkflowManagerModal';
import CustomFieldManagerModal from './Components/CustomFieldManagerModal';
import TemplateEdit from '@frontend/Profile/Components/Eng_templates/TemplateEdit';
import '@frontend/Profile/Components/Eng_templates/TemplateEdit.css';


interface CreateMattersPageProps {}

type MatterFormState = MatterFormData & { matterid?: string };

const CreateMattersPage: React.FC<CreateMattersPageProps> = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState<SelectOption[]>([]);
  const [employees, setEmployees] = useState<SelectOption[]>([]);
  const [workflows, setWorkflows] = useState<Workflow[]>([]);
  const [showWorkflowManager, setShowWorkflowManager] = useState<boolean>(false);
  const [showMoreInfoModal, setShowMoreInfoModal] = useState<boolean>(false);
  const [loadingWorkflows, setLoadingWorkflows] = useState<boolean>(false);
  // Removed showEmployeeModal - now using inline dropdown in TeamMembersPicker
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<string[]>([]);
  const [showCustomFieldsModal, setShowCustomFieldsModal] = useState<boolean>(false);
  const [showCustomFieldManager, setShowCustomFieldManager] = useState<boolean>(false);
  const [customFields, setCustomFields] = useState<CustomField[]>([]);
  const [selectedCustomFields, setSelectedCustomFields] = useState<{ [key: number]: boolean }>({});
  const [groupRules, setGroupRules] = useState<GroupRule[]>([]);
  const [showGroupRules, setShowGroupRules] = useState<boolean>(false);
  const [selectedGroupRule, setSelectedGroupRule] = useState<GroupRule | null>(null);
  const [showCustomTablesModal, setShowCustomTablesModal] = useState<boolean>(false);
  const [customTables, setCustomTables] = useState<CustomTable[]>([]);
  const [selectedCustomTables, setSelectedCustomTables] = useState<number[]>([]);
  const [matterTypeFields, setMatterTypeFields] = useState<CustomField[]>([]);

  // Derive selected custom field IDs from checkbox map for reuse across UI blocks
  const selectedFieldIds = useMemo(
    () =>
      Object.keys(selectedCustomFields)
        .filter((key) => selectedCustomFields[parseInt(key)])
        .map((key) => parseInt(key)),
    [selectedCustomFields]
  );

  const clientData = useQuery({
    queryKey: ['Clients'],
    queryFn: ClientService.getClients,
  });

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      assoc_employees: selectedTeamMembers,
    }));
  }, [selectedTeamMembers]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientData, employeeData, customFieldsResp] = await Promise.all([
          ClientService.getClients(),
          EmployeeService.getEmployees(),
          CustomFieldsService.getCustomFields(),
        ]);

        setClients(
          clientData.map((client: Client) => {
            const clientId = client.ClientID || client.clientId || '';
            const firstName = client.FirstName || client.first_name || '';
            const lastName = client.LastName || client.last_name ? ` ${client.LastName || client.last_name}` : '';
            const company = client.Company || client.company ? ` (${client.Company || client.company})` : '';
            return {
              value: clientId,
              label:
                `${clientId} - ${firstName}${lastName}${company}`.trim(),
            };
          })
        );

        setEmployees(
          employeeData.map((employee: Employees) => ({
            value: employee.EmployeeID || '',
            label: `${employee.FirstName || ''} ${employee.LastName || ''}`,
          }))
        );

        // Populate matter type options from custom fields of type 'matter_type'
        const allCF: CustomField[] = customFieldsResp?.data || [];
        const mtFields = allCF.filter(
          (f: CustomField) => (f?.type || '').toLowerCase() === 'matter_type'
        );
        setMatterTypeFields(mtFields);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
    };

    fetchData();
  }, []);

  const fetchWorkflows = async (): Promise<void> => {
    try {
      setLoadingWorkflows(true);
      const workflowsData: Workflow[] = await WorkflowService.getWorkflows();
      // Normalize API shape: backend may return `workflow_id` instead of `workflowid`.
      const normalized = (workflowsData || []).map((w: any) => ({
        ...w,
        workflowid: w.workflowid ?? w.workflow_id ?? w.workflow_id_string ?? undefined,
      }));
      setWorkflows(normalized);
      // Debug: show normalized workflows
      // eslint-disable-next-line no-console
      console.debug('fetched workflows (normalized)', normalized);
    } catch (err) {
      console.error('Error fetching workflows:', err);
    } finally {
      setLoadingWorkflows(false);
    }
  };

  // Preload workflows and custom fields so the dropdowns have data without opening modals
  useEffect(() => {
    fetchWorkflows();
    fetchCustomFields();
  }, []);

  const toggleModals = async (): Promise<void> => {
    setShowMoreInfoModal((prev) => !prev);
  };

  const toggleWorkflowModal = async (): Promise<void> => {
    if (!showWorkflowManager) {
      await fetchWorkflows();
    }
    setShowWorkflowManager((prev) => !prev);
  };

  // State for form inputs
  const [formData, setFormData] = useState<MatterFormState>({
    mattername: '',
    mattertype: '',
    matterid: '',
    clientid: '',
    employeeid: '',
    opendate: '',
    closedate: '',
    description: '',
    amount: '0',
    budget: '',
    workflowid: '',
    country: '',
    application_date: '',
    application_no: '',
    registration_date: '',
    registration_no: '',
    publication_date: '',
    publication_no: '',
    application_status: '',
    priority_data: '',
    office_action_date: '',
    expiry_date: '',
    assoc_employees: [],
    assoc_customfields: [],
  });

  // Per-cost-code hour budgets keyed by code (string values so a blank field stays blank/uncapped).
  const [codeBudgets, setCodeBudgets] = useState<Record<string, string>>({});

  // Auto-generate Matter ID
  const handleAutoGenerateMatterId = (): void => {
    const year = new Date().getFullYear();
    const randomNum = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    const matterId = `MAT-${year}-${randomNum}`;
    console.log('Auto-generated Matter ID:', matterId);
    setFormData((prev) => ({
      ...prev,
      matterid: matterId,
    }));
  };

  const handleRemoveTeamMember = (employeeIdToRemove: string) => {
    setSelectedTeamMembers((prev) =>
      prev.filter((employeeId) => employeeId !== employeeIdToRemove)
    );
  };

  const handleAddTeamMember = (employeeId: string) => {
    setSelectedTeamMembers((prev) => {
      if (prev.includes(employeeId)) return prev;
      return [...prev, employeeId];
    });
  };

  const handleFilesSelected = (files: File[]) => {
    // TODO: Wire to backend upload when endpoint is available.
    console.log('Selected files:', files);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (field: keyof MatterFormData) => (selectedOption: SelectOption | null): void => {
    setFormData((prev) => ({
      ...prev,
      [field]: selectedOption ? selectedOption.value : '',
    }));
  };

  const handleWorkflowClick = (workflowId: string): void => {
    setFormData((prevData) => ({
      ...prevData,
      workflowid: workflowId,
    }));
  };


  // Fetch custom fields
  const fetchCustomFields = async (): Promise<void> => {
    try {
      const response = await CustomFieldsService.getCustomFields();
      setCustomFields(response?.data || []);
    } catch (err) {
      console.error('Error fetching custom fields:', err);
    }
  };

  // Toggle custom field selection
  const toggleCustomFieldSelection = (fieldId: string | number): void => {
    const id = typeof fieldId === 'string' ? parseInt(fieldId) : fieldId;
    const newSelectedFields = {
      ...selectedCustomFields,
      [id]: !selectedCustomFields[id],
    };
    setSelectedCustomFields(newSelectedFields);

    // Update formData with selected custom fields as array
    const selectedFieldsArray = Object.keys(newSelectedFields)
      .filter((key) => newSelectedFields[parseInt(key)])
      .map((key) => parseInt(key));

    setFormData((prev) => ({
      ...prev,
      assoc_customfields: selectedFieldsArray,
    }));
  };

  // Fetch group rules
  const fetchGroupRules = async (): Promise<void> => {
    try {
      const response: GroupRule[] = await CustomFieldsService.getGroupRules();
      setGroupRules(response);
    } catch (err) {
      console.error('Error fetching group rules:', err);
    }
  };

  // Handle custom fields modal
  const handleCustomFieldsClick = async (): Promise<void> => {
    if (!showCustomFieldsModal) {
      await Promise.all([fetchCustomFields(), fetchGroupRules()]);
    }
    setShowCustomFieldsModal(!showCustomFieldsModal);
  };

  // Handle group rule selection
  const handleGroupRuleSelect = (groupRule: GroupRule): void => {
    setSelectedGroupRule(groupRule);
    const newSelectedFields: { [key: number]: boolean } = {};

    // Handle both array format and object format of assoc_customfields
    let selectedFieldsArray: number[];
    if (Array.isArray(groupRule.assoc_customfields)) {
      // If it's already an array, use it directly
      selectedFieldsArray = groupRule.assoc_customfields;
    } else if (groupRule.assoc_customfields) {
      // If it's an object format like {"1": true, "2": true}, convert to array
      selectedFieldsArray = Object.keys(groupRule.assoc_customfields).map(
        (id) => parseInt(id)
      );
    } else {
      selectedFieldsArray = [];
    }

    // Update UI state
    selectedFieldsArray.forEach((fieldId: number) => {
      newSelectedFields[fieldId] = true;
    });

    setSelectedCustomFields(newSelectedFields);
    setFormData((prev) => ({
      ...prev,
      assoc_customfields: selectedFieldsArray,
    }));
  };

  const fetchCustomTables = async (): Promise<void> => {
    try {
      const response = await TablesService.getCustomTables() as CustomTable[] | { data?: CustomTable[] };
      const list: CustomTable[] = Array.isArray(response) ? response : (response?.data ?? []);
      setCustomTables(list);
      // Select all tables by default
      setSelectedCustomTables(list.map((table) => table.id));
    } catch (err) {
      console.error('Error fetching custom tables:', err);
    }
  };

  // Add this after other useEffect hooks
  useEffect(() => {
    // Fetch tables on component mount
    fetchCustomTables();
  }, []); // Empty dependency array means this runs once on mount

  // Add this handler
  const handleCustomTableClick = (tableId: number): void => {
    setSelectedCustomTables((prevSelected) => {
      if (prevSelected.includes(tableId)) {
        return prevSelected.filter((id) => id !== tableId);
      } else {
        return [...prevSelected, tableId];
      }
    });
  };

  // Mutation for creating matter
  const createMatterMutation = useMutation({
    mutationFn: (formData: MatterFormData) => MatterService.addMatter(formData),
    onSuccess: async (data: any) => {
      console.log('Matter created successfully:', data);
      // Persist any per-cost-code budgets against the new matter. The server generates the id
      // (clientid-NNNNN) and echoes it in the response envelope at data.data.newMatterID — the form's
      // matterid is NOT what gets stored, so read the server echo and only fall back to it. A blank/0
      // field is skipped (uncapped). Budget writes are admin-only server-side; a non-admin creator
      // just gets no budgets (non-fatal).
      const newMatterId = data?.data?.newMatterID || data?.newMatterID || formData.matterid;
      const budgetRows = Object.entries(codeBudgets)
        .map(([cost_code, raw]) => ({ cost_code, budget_hours: parseFloat(String(raw)) }))
        .filter((r) => Number.isFinite(r.budget_hours) && r.budget_hours > 0);
      if (newMatterId && budgetRows.length > 0) {
        try {
          await MatterCostCodeBudgetService.saveBudgets(newMatterId, budgetRows);
        } catch (e) {
          console.error('Matter created, but cost code budgets could not be saved:', e);
          toastService.warning('Matter created, but cost code budgets were not saved.', {
            description: 'Only administrators can set budgets. You can set them later from the timesheet.',
            duration: 5000,
          });
        }
      }
      toastService.success('Matter created successfully!', {
        description: `Matter "${formData.mattername}" has been created.`,
        duration: 4000,
      });
      // Navigate to matters list after successful creation
      setTimeout(() => navigate('/matters'), 1500);
    },
    onError: (error: any) => {
      console.error('Error creating Matter:', error);
      const errorMessage = error?.response?.data?.error ?? error?.data?.error ?? error?.response?.data?.message ?? error?.data?.message ?? error?.message ?? 'An unexpected error occurred';
      toastService.error('Failed to create matter', {
        description: errorMessage,
        duration: 5000,
      });
    },
  });

  const handleSubmit = (): void => {
    // Validate required fields
    if (!formData.mattername?.trim()) {
      toastService.warning('Please enter a matter name', { duration: 3000 });
      return;
    }
    if (!formData.clientid) {
      toastService.warning('Please select a client', { duration: 3000 });
      return;
    }
    if (!formData.employeeid) {
      toastService.warning('Please select a matter leader', { duration: 3000 });
      return;
    }
    if (!formData.opendate) {
      toastService.warning('Please select a creation date', { duration: 3000 });
      return;
    }
    if (!formData.closedate) {
      toastService.warning('Please select a due date', { duration: 3000 });
      return;
    }
    if (!formData.workflowid) {
      toastService.warning('Please select a workflow', { duration: 3000 });
      return;
    }

    // Show loading toast
    toastService.info('Creating matter...', { duration: 2000 });

    // The overall project budget is DERIVED as the sum of the per-cost-code budgets. It is set by
    // the admin-gated PUT below (which re-syncs matters.budget server-side), so we do NOT seed an
    // independent budget here — a non-admin creator can't persist code rows, so their matters.budget
    // correctly stays unset/uncapped rather than showing a total with no backing breakdown.
    createMatterMutation.mutate({ ...formData, budget: '' });
  };

  return (
    <div className="create-matters-page">
      {/* Custom Top Bar with Title, Subtitle, and Tabs */}
      <div className="create-matters-top-bar">
        <div className="create-matters-top-bar-content">
          <div className="create-matters-header-left">
            <button
              className="create-matters-back-button"
              onClick={() => navigate('/matters')}
              aria-label="Back to matters"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="create-matters-header-text">
              <h1 className="create-matters-title">Create New Matter</h1>
              <p className="create-matters-subtitle">Set up a new legal matter with all necessary details</p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Form Container */}
      <div className="create-matters-form-container">
        <div className="create-matters-sections">
          <SectionCard
            title="Required Details"
            subtitle="Essential information needed to create the matter"
            className="create-matters-section"
          >
            {/* Matter Name - Full Width */}
            <div className="create-matters-full-width-field">
              <FormInput
                label="Matter Name"
                name="mattername"
                type="text"
                placeholder="Enter matter name"
                value={formData.mattername}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* First Row: Matter ID and Matter Leader */}
            <div className="create-matters-form-grid-2x2">
              {/* Matter ID with Auto-Generate */}
              <div className="form-group form-input-group">
                <label htmlFor="matterid" className="form-group-label">
                  Matter ID
                  <span className="required-asterisk">*</span>
                </label>
                <div className="matter-id-input-wrapper">
                  <input
                    id="matterid"
                    type="text"
                    name="matterid"
                    className="form-input matter-id-input"
                    placeholder="Leave blank to auto-generate"
                    value={formData.matterid}
                    onChange={handleInputChange}
                  />
                  <AutoGenerateButton
                    onClick={handleAutoGenerateMatterId}
                    className="matter-id-auto-generate"
                  />
                </div>
              </div>

              {/* Matter Leader */}
              <FormSelect
                label="Matter Leader"
                name="employeeid"
                options={employees}
                value={formData.employeeid}
                onChange={(value) => setFormData((prev) => ({ ...prev, employeeid: value }))}
                placeholder="Select leader..."
                isClearable
                required
              />
            </div>

            {/* Second Row: Creation Date and Due Date */}
            <div className="create-matters-form-grid-2x2">
              {/* Creation Date */}
              <FormInput
                label="Creation Date"
                name="opendate"
                type="date"
                placeholder="mm/dd/yyyy"
                value={formData.opendate}
                onChange={handleInputChange}
                required
              />

              {/* Due Date */}
              <FormInput
                label="Due Date"
                name="closedate"
                type="date"
                placeholder="mm/dd/yyyy"
                value={formData.closedate}
                onChange={handleInputChange}
                required
              />
            </div>

            {/* Per-cost-code hour budgets (optional). The overall project budget is the sum of these. */}
            <div className="create-matters-full-width-field">
              <label className="form-group-label">Cost Code Budgets (hrs)</label>
              <CostCodeBudgetInputs
                values={codeBudgets}
                onChange={(code, value) =>
                  setCodeBudgets((prev) => ({ ...prev, [code]: value }))
                }
              />
            </div>

            {/* Add Client - Full Width */}
            <div className="create-matters-full-width-field">
              <div className="form-group form-select-group">
                <FormSelect
                  label="Add Client"
                  name="clientid"
                  options={clients}
                  value={formData.clientid}
                  onChange={(value) => setFormData((prev) => ({ ...prev, clientid: value }))}
                  placeholder="Select client..."
                  isClearable
                  isLoading={clientData.isLoading}
                  required
                />
                <button
                  type="button"
                  className="create-new-link"
                  onClick={() => navigate('/createClient')}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create New
                </button>
              </div>
            </div>

            {/* Add Workflow - Full Width */}
            <div className="create-matters-full-width-field">
              <div className="form-group form-select-group">
                <FormSelect
                  label="Add Workflow"
                  name="workflowid"
                  options={workflows
                    .filter((w) => !!w?.workflowid)
                    .map((w) => ({
                      value: String(w.workflowid),
                      label: w.name,
                    }))}
                  value={formData.workflowid}
                  onChange={(value) => setFormData((prev) => ({ ...prev, workflowid: value }))}
                  placeholder="Select workflow template..."
                  isClearable
                  isLoading={loadingWorkflows}
                  required
                />
                <button
                  type="button"
                  className="create-new-link"
                  onClick={toggleWorkflowModal}
                >
                  <FontAwesomeIcon icon={faPlus} /> Create New
                </button>
              </div>
            </div>

            {/* Add Custom Field - Full Width */}
            <div className="create-matters-full-width-field">
              <div className="form-group form-select-group">
                <FormSelect
                  label="Add Custom Field"
                  name="customfield"
                  options={customFields
                    .filter((cf) => !selectedFieldIds.includes(
                      typeof cf.customfield_id === 'string' ? parseInt(cf.customfield_id) : cf.customfield_id
                    ))
                    .map((cf) => ({
                      value: cf.customfield_id.toString(),
                      label: cf.custom_field,
                    }))}
                  value=""
                  onChange={(value) => {
                    if (value) {
                      toggleCustomFieldSelection(parseInt(value));
                    }
                  }}
                  placeholder={selectedFieldIds.length > 0 ? `${selectedFieldIds.length} field(s) selected - add more...` : "Select field to add..."}
                  isClearable
                />
                <button
                  type="button"
                  className="create-new-link"
                  onClick={() => setShowCustomFieldManager(true)}
                >
                  <FontAwesomeIcon icon={faPlus} /> Manage
                </button>
              </div>
              {/* Show selected custom fields as chips */}
              {selectedFieldIds.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {selectedFieldIds.map((fieldId) => {
                    const field = customFields.find(
                      (cf) => (typeof cf.customfield_id === 'string' ? parseInt(cf.customfield_id) : cf.customfield_id) === fieldId
                    );
                    return field ? (
                      <span
                        key={fieldId}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 10px',
                          background: '#eef2ff',
                          border: '1px solid #c7d2fe',
                          borderRadius: 8,
                          fontSize: 13,
                          color: '#4338ca',
                          fontWeight: 500,
                        }}
                      >
                        {field.custom_field}
                        <button
                          type="button"
                          onClick={() => toggleCustomFieldSelection(fieldId)}
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            cursor: 'pointer',
                            color: '#6366f1',
                            display: 'flex',
                            alignItems: 'center',
                            fontSize: 12,
                          }}
                        >
                          <FontAwesomeIcon icon={faTimes} />
                        </button>
                      </span>
                    ) : null;
                  })}
                </div>
              )}
            </div>

          </SectionCard>

          <SectionCard
            title="Custom Fields and Tables"
            subtitle="Select custom fields and tables to include in this matter"
            className="create-matters-section"
          >
            <CustomFieldsTablesPanel
              customTables={(Array.isArray(customTables) ? customTables : []).map((t) => ({
                id: t.id,
                name: t.table_name,
                description: 'Table for detailed configuration',
              }))}
              customFields={customFields.map((f) => ({
                id:
                  typeof f.customfield_id === 'string'
                    ? parseInt(f.customfield_id)
                    : f.customfield_id,
                name: f.custom_field,
                description: 'Risk level evaluation field',
              }))}
              selectedTableIds={selectedCustomTables}
              selectedFieldIds={selectedFieldIds}
              onToggleTable={handleCustomTableClick}
              onToggleField={toggleCustomFieldSelection}
              onAddGlobalTable={() => setShowCustomTablesModal(true)}
            />
          </SectionCard>

          <SectionCard
            title="Optional & Customization"
            subtitle="Additional details and custom configurations"
            className="create-matters-section"
          >
            <div className="create-matters-optional-stack">
              <FormEntryMultilineTextbox
                label="Description/Details"
                name="description"
                placeholder="Enter matter description, objectives, and key details..."
                value={formData.description}
                onChange={handleInputChange}
              />

              <TeamMembersPicker
                options={employees}
                selectedIds={selectedTeamMembers}
                onRemove={handleRemoveTeamMember}
                onAdd={handleAddTeamMember}
              />

              <FileDropzone onFilesSelected={handleFilesSelected} />
            </div>

            {/* Submit Button */}
            <div className="create-matters-form-actions">
              <button
                type="button"
                className="create-matters-submit-button"
                onClick={handleSubmit}
              >
                + Create Matter
              </button>
              <button
                type="button"
                className="create-matters-save-draft-button"
                onClick={() => console.log('Save draft')}
              >
                Save Draft
              </button>
            </div>
          </SectionCard>
        </div>
      </div>

      {/* Legacy Side Buttons Section (kept temporarily for backward compatibility)
          The redesigned UI renders these features inline inside the main card above. */}
      {/* <div className="m-side-buttons">
          <button
            type="button"
            className="m-side-button side1"
            onClick={handleAddTeamMembersClick}
          >
            Add Team Members <FontAwesomeIcon icon={faUsers} />
          </button>
          <button
            type="button"
            className="m-side-button side2"
            onClick={handleCustomFieldsClick}
          >
            Select Custom Fields <FontAwesomeIcon icon={faPlus} />
          </button>
          <button
            type="button"
            className="m-side-button side3"
            onClick={toggleModals}
          >
            More Information <FontAwesomeIcon icon={faChevronDown} />
          </button>
          <button
            type="button"
            className="m-side-button side4"
            onClick={() => setShowCustomTablesModal(true)}
          >
            Add Custom Tables <FontAwesomeIcon icon={faPlus} />
          </button>
          <button
            type="button"
            className="m-side-button side5"
            onClick={toggleWorkflowModal}
          >
            Add Workflow <FontAwesomeIcon icon={faChevronDown} />
          </button>
          <button
            type="button"
            className="m-side-button side6"
            onClick={() =>
              alert('Add Documents functionality not implemented yet.')
            }
          >
            Add Documents <FontAwesomeIcon icon={faPlus} />
          </button>
        </div> */}

      {/* More Information Modal */}
      {showMoreInfoModal && (
        <div className="create-modal-overlay" onClick={toggleModals}>
          <div
            className="create-modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="create-modal-header">
              <h2>Add Additional Information</h2>
              <button className="create-modal-close" onClick={toggleModals}>
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <form>
              <div className="m-form-group">
                <label className="m-form-group-label">Country</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="country"
                    value={formData.country}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Application Date</label>
                <div className="m-input-icon">
                  <input
                    type="date"
                    name="application_date"
                    value={formData.application_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Application No</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="application_no"
                    value={formData.application_no}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Registration Date</label>
                <div className="m-input-icon">
                  <input
                    type="date"
                    name="registration_date"
                    value={formData.registration_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Registration No</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="registration_no"
                    value={formData.registration_no}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Publication Date</label>
                <div className="m-input-icon">
                  <input
                    type="date"
                    name="publication_date"
                    value={formData.publication_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Publication No</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="publication_no"
                    value={formData.publication_no}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Application Status</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="application_status"
                    value={formData.application_status}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Priority Data</label>
                <div className="m-input-icon">
                  <input
                    type="text"
                    name="priority_data"
                    value={formData.priority_data}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Office Action Date</label>
                <div className="m-input-icon">
                  <input
                    type="date"
                    name="office_action_date"
                    value={formData.office_action_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
              <div className="m-form-group">
                <label className="m-form-group-label">Expiry Date</label>
                <div className="m-input-icon">
                  <input
                    type="date"
                    name="expiry_date"
                    value={formData.expiry_date}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Workflow Manager Modal */}
      <WorkflowManagerModal
        isOpen={showWorkflowManager}
        onClose={toggleWorkflowModal}
        selectedWorkflowId={formData.workflowid}
        onSelect={(id: string) => setFormData((prev) => ({ ...prev, workflowid: id }))}
        onWorkflowsChanged={fetchWorkflows}
      />

      {/* Custom Field Manager Modal */}
      <CustomFieldManagerModal
        isOpen={showCustomFieldManager}
        onClose={() => setShowCustomFieldManager(false)}
        selectedFieldIds={selectedFieldIds}
        onToggleField={toggleCustomFieldSelection}
        onFieldsChanged={fetchCustomFields}
      />

      {/* Team Members Modal removed - now using inline dropdown in TeamMembersPicker */}

      {/* Custom Fields Modal */}
      {showCustomFieldsModal && (
        <div className="create-modal-overlay" onClick={handleCustomFieldsClick}>
          <div
            className="CM-custom-fields-popout"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-header">
              <h3>Select Custom Fields</h3>
              <div className="modal-actions">
                <button
                  className="toggle-view-button"
                  onClick={() => setShowGroupRules(!showGroupRules)}
                >
                  {showGroupRules ? 'View Fields' : 'View Groups'}
                  <FontAwesomeIcon
                    icon={showGroupRules ? faLayerGroup : faChevronDown}
                  />
                </button>
                <button
                  className="close-modal"
                  onClick={handleCustomFieldsClick}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            </div>

            {showGroupRules ? (
              <div className="group-rules-list">
                {groupRules.length === 0 ? (
                  <p className="no-groups-message">No groups available.</p>
                ) : (
                  <ul>
                    {groupRules.map((group) => (
                      <li
                        key={group.id}
                        className={`group-rule-item ${
                          selectedGroupRule?.id === group.id ? 'selected' : ''
                        }`}
                        onClick={() => handleGroupRuleSelect(group)}
                      >
                        <div className="group-header">
                          <h4>{group.group_name}</h4>
                        </div>
                        <div className="group-fields">
                          {Array.isArray(group.assoc_customfields)
                            ? // If assoc_customfields is an array
                              group.assoc_customfields.map((fieldId) => {
                                const field = customFields.find(
                                  (f) => f.customfield_id === fieldId
                                );
                                return field ? (
                                  <span
                                    key={fieldId}
                                    className="group-field-tag"
                                  >
                                    {field.custom_field}
                                  </span>
                                ) : null;
                              })
                            : group.assoc_customfields
                            ? // If assoc_customfields is an object
                              Object.keys(group.assoc_customfields).map(
                                (fieldId) => {
                                  const field = customFields.find(
                                    (f) =>
                                      f.customfield_id === parseInt(fieldId)
                                  );
                                  return field ? (
                                    <span
                                      key={fieldId}
                                      className="group-field-tag"
                                    >
                                      {field.custom_field}
                                    </span>
                                  ) : null;
                                }
                              )
                            : null}
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : (
              <div className="custom-fields-list">
                {customFields.length === 0 ? (
                  <p className="no-fields-message">
                    No custom fields available.
                  </p>
                ) : (
                  <ul>
                    {customFields.map((field) => (
                      <li
                        key={field.customfield_id}
                        className="custom-field-item"
                      >
                        <div className="field-checkbox">
                          <input
                            type="checkbox"
                            checked={
                              !!selectedCustomFields[typeof field.customfield_id === 'string' ? parseInt(field.customfield_id) : field.customfield_id]
                            }
                            onChange={() =>
                              toggleCustomFieldSelection(field.customfield_id)
                            }
                          />
                        </div>
                        <div className="field-content">
                          <p>{field.custom_field}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Custom Tables Modal */}
      {showCustomTablesModal && (
        <div
          className="create-modal-overlay"
          onClick={() => setShowCustomTablesModal(false)}
          style={{ zIndex: 10001 }}
        >
          <div
            className="template-edit-modal-container"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="template-edit-modal-header">
              <h2>Manage Custom Tables</h2>
              <button
                className="template-edit-modal-close"
                onClick={() => {
                  setShowCustomTablesModal(false);
                  // Refresh tables when closing to include newly created tables.
                  fetchCustomTables();
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            <div className="template-edit-modal-content">
              <TemplateEdit permishId={null} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CreateMattersPage;
